import AddIcon from '@mui/icons-material/Add';
import { Alert, AlertTitle, Box, Button, MenuItem, Paper, Skeleton, Stack, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import * as React from 'react';

import { ContextStore } from '../../store/contextStore';
import { useDeveloperApi } from '../api';
import { DeveloperShell, ErrorAlert } from '../components';
import type { Integration, IntegrationList, IntegrationRemoval } from '../integrationTypes';
import AddConnectionFlow from './AddConnectionFlow';
import ConnectionCard from './ConnectionCard';
import FleetCard from './FleetCard';
import { IntegrationsContext, IntegrationsPageContext } from './IntegrationsContext';
import { REMOVAL_VENDOR_WEBHOOKS, UNKNOWN_SLUG } from './integrationLabels';

/** An account as the removal note names it (`_default` is the Default account). */
export const accountName = (slug: string) => (slug === '_default' ? 'Default' : slug);

type RemovalNote = { key: number; what: string; note: IntegrationRemoval };

/** A connection without I2's `_unknown` pseudo-account (a webhook to an account we do not know): never a row. */
const withoutUnknown = (connection: Integration): Integration => (
    connection.accounts.some((a) => a.slug === UNKNOWN_SLUG)
        ? { ...connection, accounts: connection.accounts.filter((a) => a.slug !== UNKNOWN_SLUG) }
        : connection
);

/** The list as the page shows it: no `_unknown` pseudo-connection or pseudo-account, whatever a backend sends. */
export const visibleList = (list: IntegrationList): IntegrationList => ({
    ...list,
    connections: list.connections.filter((c) => c.id !== UNKNOWN_SLUG && c.system !== UNKNOWN_SLUG).map(withoutUnknown),
});

/** Stable, never by health (plan §1 Layout): booking systems by name, then fleet. */
const byName = (a: Integration, b: Integration) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id);

/** The loading state: the shape of a card, not a spinner in the middle of the page. */
const CardSkeleton = () => (
    <Paper variant="outlined" sx={{ p: 2 }} aria-hidden>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Skeleton variant="text" width={120} height={28} />
            <Skeleton variant="rounded" width={90} height={24} />
        </Stack>
        <Skeleton variant="text" width="55%" />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 1, my: 1.5 }}>
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={52} />)}
        </Box>
        <Skeleton variant="text" width="35%" />
        <Skeleton variant="text" width="70%" />
    </Paper>
);

const EmptyState = ({ canAdd, onAdd }: { canAdd: boolean; onAdd: () => void }) => (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }} data-testid="integrations-empty">
        <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 500 }}>No booking system connected</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: '68ch' }}>
            Connect the booking system you sell with, and its bookings arrive in TourOptima by themselves: trips, guests
            and their messages follow from them.
        </Typography>
        {canAdd ? (
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={onAdd} sx={{ mt: 2 }}>Add booking system</Button>
        ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                Connecting one needs the permission “Import: integrations”: ask an administrator.
            </Typography>
        )}
    </Paper>
);

/** Developer › Integrations (DSEC INT plan v3 §1): "is my booking system connected, and are bookings flowing?"
 *  answered first by a status card per connection, then fixed on the card itself (keys, accounts, webhook
 *  addresses and token, client settings), or a system added or removed. A franchise parent's user picks the
 *  licensee; every call carries it as `franchise_to`. */
const IntegrationsPage = () => {
    const developerApi = useDeveloperApi();
    const { enqueueSnackbar } = useSnackbar();
    const { isFranchise, currentLicensee } = React.useContext(ContextStore) as any;
    const [franchiseTo, setFranchiseTo] = React.useState<number | null>(isFranchise && currentLicensee ? Number(currentLicensee) : null);
    const api = React.useMemo(() => developerApi.integrationsFor(franchiseTo), [developerApi, franchiseTo]);
    const [list, setList] = React.useState<IntegrationList | null>(null);
    const [error, setError] = React.useState<unknown>(null);
    const [loading, setLoading] = React.useState(true);
    const [adding, setAdding] = React.useState(false);
    const [justAdded, setJustAdded] = React.useState<string | null>(null);
    const [notes, setNotes] = React.useState<RemovalNote[]>([]);
    const generation = React.useRef(0);
    const noteKey = React.useRef(0);
    const noteRemoval = React.useCallback((what: string, note: IntegrationRemoval | null) => {
        if (note && note.vendor_webhooks) {
            noteKey.current += 1;
            setNotes((prev) => [...prev, { key: noteKey.current, what, note }]);
        }
    }, []);

    const load = React.useCallback(async () => {
        const mine = ++generation.current;
        setLoading(true);
        setError(null);
        try {
            const answer = await api.list();
            if (mine === generation.current) {
                setList(visibleList(answer));
            }
        } catch (e) {
            if (mine === generation.current) {
                setError(e);
            }
        } finally {
            if (mine === generation.current) {
                setLoading(false);
            }
        }
    }, [api]);

    React.useEffect(() => {
        load();
    }, [load]);

    const showTenant = React.useCallback((tenantId: number) => {
        setList(null);
        setAdding(false);
        setJustAdded(null);
        setNotes([]);
        setFranchiseTo(tenantId);
    }, []);

    const context: IntegrationsPageContext | null = React.useMemo(() => (list ? {
        api,
        environment: list.environment,
        permissions: list.permissions,
        tenant: list.tenant,
        trackedSince: list.metrics.tracked_since ?? null,
        replace: (connection: Integration) => setList((prev) => (prev ? {
            ...prev,
            connections: prev.connections.map((c) => (c.id === connection.id ? withoutUnknown(connection) : c)),
        } : prev)),
        reload: load,
        noteRemoval,
    } : null), [api, list, load, noteRemoval]);

    const addable = (list?.available_systems || []).filter((s) => !s.connected);
    const canAdd = !!list?.permissions.edit && addable.some((s) => s.addable);
    const hasBooking = !!list?.connections.some((c) => c.kind !== 'fleet');
    const addButton = canAdd && !adding && hasBooking ? (
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAdding(true)}>Add booking system</Button>
    ) : undefined;

    if (!list || !context) {
        return (
            <DeveloperShell title="Integrations">
                <Box sx={{ maxWidth: 960, mt: 1 }}>
                    {loading || !error ? (
                        <Box role="status" aria-busy="true" aria-label="Loading the integrations" data-testid="integrations-loading">
                            <Stack spacing={2}><CardSkeleton /></Stack>
                        </Box>
                    ) : <ErrorAlert error={error} onRetry={load} />}
                </Box>
            </DeveloperShell>
        );
    }

    const booking = list.connections.filter((c) => c.kind !== 'fleet').sort(byName);
    const fleet = list.connections.filter((c) => c.kind === 'fleet').sort(byName);
    const licensees = list.licensees && list.licensees.length > 1 ? list.licensees : null;

    return (
        <IntegrationsContext.Provider value={context}>
            <DeveloperShell title="Integrations" actions={addButton}>
                <Box sx={{ maxWidth: 960 }}>
                    {licensees && (
                        <TextField
                            select
                            size="small"
                            label="Company"
                            value={String(list.tenant.id)}
                            onChange={(ev) => showTenant(Number(ev.target.value))}
                            SelectProps={{ MenuProps: { sx: { zIndex: 10002 } } }}
                            sx={{ my: 1, minWidth: 260 }}
                            inputProps={{ 'data-testid': 'licensee-picker' }}
                        >
                            {licensees.map((t) => <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>)}
                        </TextField>
                    )}
                    {list.environment === 'sandbox' && (
                        <Typography variant="body2" color="text.secondary" sx={{ my: 1, maxWidth: '75ch' }} data-testid="sandbox-note">
                            This sandbox has its own connections: credentials are not copied from production, the webhook addresses
                            are the sandbox&apos;s, and nothing is written at a booking system from here.
                        </Typography>
                    )}
                    {!list.permissions.edit && (
                        <Typography variant="body2" color="text.secondary" sx={{ my: 1 }} data-testid="read-only-note">
                            You can view these connections. Changing them needs the permission “Import: integrations”: ask an administrator.
                        </Typography>
                    )}
                    {error ? <ErrorAlert error={error} onRetry={load} /> : null}
                    {notes.map(({ key, what, note }) => (
                        <Alert
                            key={key}
                            severity="warning"
                            sx={{ my: 1 }}
                            data-testid="removal-note"
                            onClose={() => setNotes((prev) => prev.filter((n) => n.key !== key))}
                        >
                            <AlertTitle>{what} removed</AlertTitle>
                            {note.message || REMOVAL_VENDOR_WEBHOOKS[note.vendor_webhooks] || note.vendor_webhooks}
                            {note.accounts.length > 0 && (
                                <Box component="span" sx={{ display: 'block', mt: 0.5 }}>Accounts: {note.accounts.map(accountName).join(', ')}</Box>
                            )}
                        </Alert>
                    ))}

                    <Stack spacing={2} sx={{ mt: 1.5 }}>
                        {adding && (
                            <AddConnectionFlow
                                systems={list.available_systems}
                                onClose={() => setAdding(false)}
                                onAdded={(connection) => {
                                    setAdding(false);
                                    setJustAdded(connection.id);
                                    setList((prev) => (prev ? { ...prev, connections: [...prev.connections.filter((c) => c.id !== connection.id), withoutUnknown(connection)] } : prev));
                                    enqueueSnackbar(`${connection.name} added`, { variant: 'success' });
                                    // The list again: the systems offered and every count follow the addition.
                                    load();
                                }}
                            />
                        )}
                        {booking.length === 0 && !adding && <EmptyState canAdd={canAdd} onAdd={() => setAdding(true)} />}
                        {booking.map((connection) => (
                            <ConnectionCard
                                key={connection.id}
                                connection={connection}
                                justAdded={justAdded === connection.id}
                                onShowTenant={licensees && connection.inherited_from && licensees.some((t) => t.id === connection.inherited_from?.id) ? showTenant : undefined}
                            />
                        ))}
                    </Stack>

                    {fleet.length > 0 && (
                        <Box component="section" aria-labelledby="fleet-heading" sx={{ mt: 4 }}>
                            <Typography id="fleet-heading" variant="subtitle1" component="h2" sx={{ fontWeight: 500, mb: 1.5 }}>Fleet tracking</Typography>
                            <Stack spacing={2}>
                                {fleet.map((connection) => <FleetCard key={connection.id} connection={connection} />)}
                            </Stack>
                        </Box>
                    )}
                    <Box sx={{ mt: 2 }}>
                        <Button size="small" onClick={load} disabled={loading}>Refresh</Button>
                    </Box>
                </Box>
            </DeveloperShell>
        </IntegrationsContext.Provider>
    );
};

export default IntegrationsPage;
